import { useTranslation } from "react-i18next";
import { useState } from "react";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import ModelCard from "./ModelCard";
import useModelCounts from "/app/src/hooks/useModelCounts";

/**
 * Renders the ModelView component which displays explorer sections for orders, materials, locations, and power pick settings.
 *
 * @returns {JSX.Element} The rendered model view component.
 */
export default function ModelView() {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);

  const {
    ordersCount,
    orderLinesCount,
    transactionsCount,
    batchesCount,
    materialsCount,
    materialPropertiesCount,
    locationsCount,
    warehousesCount,
    zonesCount,
    binsCount,
    costCentersCount,
    parameterValuesCount,
  } = useModelCounts();

  const orderLinks = [
    {
      path: "/explorer/batches",
      label: "Batches",
      count: batchesCount,
      icon: "BatchIcon",
    },

    {
      path: "/explorer/orders",
      label: "Orders",
      count: ordersCount,
      icon: "OrderIcon",
    },
    {
      path: "/explorer/order_lines",
      label: "Order Lines",
      count: orderLinesCount,
      icon: "OrderLineIcon",
    },
    {
      path: "/explorer/transactions",
      label: "Transactions",
      count: transactionsCount,
      icon: "TransactionIcon",
    },
  ];

  const materialLinks = [
    {
      path: "/explorer/materials",
      label: "Materials",
      count: materialsCount,
      icon: "MaterialIcon",
    },
    {
      path: "/explorer/material_properties",
      label: "Material Properties",
      count: materialPropertiesCount,
      icon: "MaterialPropertyIcon",
    },
  ];

  const locationLinks = [
    {
      path: "/explorer/bins",
      label: "Bins",
      count: binsCount,
      icon: "BinIcon",
    },
    {
      path: "/explorer/locations",
      label: "Locations",
      count: locationsCount,
      icon: "LocationIcon",
    },
    {
      path: "/explorer/warehouses",
      label: "Warehouses",
      count: warehousesCount,
      icon: "WarehouseIcon",
    },
    {
      path: "/explorer/zones",
      label: "Zones",
      count: zonesCount,
      icon: "ZoneIcon",
    },
  ];

  const powerPickLinks = [
    {
      path: "/explorer/cost_centers",
      label: "Cost Centers",
      count: costCentersCount,
      icon: "CostCenterIcon",
    },
    {
      path: "/explorer/parameter_values",
      label: "Parameter Values",
      count: parameterValuesCount,
      icon: "ParameterValueIcon",
    },
  ];

  return (
    <>
      <h1 className="mb-4">{t("translation:explorer")}</h1>
      <h2 className="text-[20px] text-neutral-smoke">
        {t("translation:orders")}
      </h2>
      <GridWrapper responsive={{ sm: 1, md: 2, lg: 4 }}>
        {orderLinks.map((link) => (
          <ModelCard
            key={link.path}
            link={link}
            hovered={hovered}
            setHovered={setHovered}
          />
        ))}
      </GridWrapper>
      <h2 className="text-[20px] mt-8 text-neutral-smoke">
        {t("translation:materials")}
      </h2>
      <GridWrapper responsive={{ sm: 1, md: 2, lg: 4 }}>
        {materialLinks.map((link) => (
          <ModelCard
            key={link.path}
            link={link}
            hovered={hovered}
            setHovered={setHovered}
          />
        ))}
      </GridWrapper>
      <h2 className="text-[20px] mt-8 text-neutral-smoke">
        {t("translation:locations")}
      </h2>
      <GridWrapper responsive={{ sm: 1, md: 2, lg: 4 }}>
        {locationLinks.map((link) => (
          <ModelCard
            key={link.path}
            link={link}
            hovered={hovered}
            setHovered={setHovered}
          />
        ))}
      </GridWrapper>
      <h2 className="text-[20px] mt-8 text-neutral-smoke">
        {t("translation:power_pick_settings")}
      </h2>
      <GridWrapper responsive={{ sm: 1, md: 2, lg: 4 }}>
        {powerPickLinks.map((link) => (
          <ModelCard
            key={link.path}
            link={link}
            hovered={hovered}
            setHovered={setHovered}
          />
        ))}
      </GridWrapper>
    </>
  );
}
